import {
  Alert,
  Button,
  Checkbox,
  Form,
  InputNumber,
  Modal,
  Switch,
  Table,
} from 'antd';
import { useContext, useState } from 'react';

import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import type { DayOfWeek } from 'app/typings/core/admin/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/admin/enum/dayOfWeek';
import type { DayOfWeekStockConfiguration } from 'app/typings/core/common/openingConfiguration/stockConfigurationType';
import { capitalizeFirstLetter } from 'app/utils/string';

import {
  markFormDirty,
  useValidationErrors,
} from '../../../hook/useValidationStore';

const DAYS_FIELD_NAME = ['openingConfiguration', 'stockConfiguration', 'days'];

export const StockConfigurationTable = () => {
  const form = Form.useFormInstance();
  const { isEditMode, isViewMode } = useContext(ViewEditModeContext);
  const validationErrors = useValidationErrors();

  const isAlwaysIncludedWithNight = Form.useWatch(
    'isAlwaysIncludedWithNight',
    form
  );

  const formValues = Form.useWatch<DayOfWeekStockConfiguration>(
    DAYS_FIELD_NAME,
    form
  );

  const [showToggleModal, setShowToggleModal] = useState(false);
  const [hasShownToggleModal, setHasShownToggleModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{
    day: DayOfWeek;
    checked: boolean;
  } | null>(null);

  const isUnlimitedStock = Object.values(formValues ?? {}).some(
    (day) => (day.stock === null || day.stock === undefined) && !day.isClosed
  );
  const showUnlimitedRow = isAlwaysIncludedWithNight && isUnlimitedStock;

  const applyToggleChange = (day: DayOfWeek, checked: boolean) => {
    markFormDirty();
    form.setFieldsValue({
      openingConfiguration: {
        stockConfiguration: {
          days: {
            [day]: {
              isClosed: !checked,
              stock: !checked ? 0 : formValues?.[day]?.stock ?? undefined,
            },
          },
        },
      },
    });
  };

  const handleToggleDay = (day: DayOfWeek, checked: boolean) => {
    if (!checked && !hasShownToggleModal) {
      setPendingToggle({ day, checked });
      setShowToggleModal(true);
    } else {
      applyToggleChange(day, checked);
    }
  };

  const handleToggleModalConfirm = () => {
    setHasShownToggleModal(true);

    if (pendingToggle) {
      applyToggleChange(pendingToggle.day, pendingToggle.checked);
    }

    setShowToggleModal(false);
    setPendingToggle(null);
  };

  const handleToggleModalCancel = () => {
    setShowToggleModal(false);
    setPendingToggle(null);
  };

  const getDataSource = () => {
    if (showUnlimitedRow) {
      return [{ key: 'stock-row' }];
    }

    if (!isAlwaysIncludedWithNight) {
      return [{ key: 'stock-row' }, { key: 'checkbox-row' }];
    }

    return [];
  };

  const columns = Object.values(DAY_OF_WEEK).map((day) => {
    const isClosed = formValues?.[day]?.isClosed ?? false;
    const isEnabled = !isClosed;

    const dayIsOnError = validationErrors.stockError.errors.some((error) =>
      error.days?.includes(day)
    );

    return {
      title: (
        <div className="table-header-title">
          <Form.Item name={[...DAYS_FIELD_NAME, day, 'isClosed']} noStyle>
            <Switch
              size="small"
              checked={isEnabled}
              onChange={(checked) => handleToggleDay(day, checked)}
              disabled={isViewMode}
            />
          </Form.Item>
          {capitalizeFirstLetter(day)}
        </div>
      ),
      dataIndex: day,
      key: day.toLowerCase(),
      className: isEnabled ? '' : 'disabled-cell',
      render: (_: unknown, record: { key: string }) => {
        const isDayUnlimited = formValues?.[day]?.stock === null;
        const fieldName = [...DAYS_FIELD_NAME, day, 'stock'];

        if (record.key === 'checkbox-row') {
          const handleDayUnlimitedToggle = () => {
            markFormDirty();
            form.setFieldsValue({
              openingConfiguration: {
                stockConfiguration: {
                  days: {
                    [day]: {
                      stock: isDayUnlimited ? 0 : null,
                    },
                  },
                },
              },
            });
          };

          return (
            <Checkbox
              checked={isDayUnlimited}
              onChange={handleDayUnlimitedToggle}
              disabled={isViewMode}
            >
              Unlimited (∞)
            </Checkbox>
          );
        }

        if (showUnlimitedRow) {
          return <span className="unlimited-stock-value">Unlimited (∞)</span>;
        }

        if (isDayUnlimited) {
          return (
            <Form.Item name={fieldName}>
              <InputNumber disabled placeholder="∞" className="stock-input" />
            </Form.Item>
          );
        }

        return (
          <Form.Item
            name={fieldName}
            rules={
              isEnabled
                ? [
                    {
                      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
                      validator: (_, value) => {
                        if (value === null) {
                          return Promise.resolve();
                        }

                        if (value === undefined) {
                          return Promise.reject(new Error('Required'));
                        }

                        return Promise.resolve();
                      },
                    },
                  ]
                : []
            }
          >
            {isEditMode ? (
              <InputNumber
                type="number"
                disabled={!isEnabled || isViewMode}
                className={'stock-input'}
                status={dayIsOnError ? 'error' : ''}
                min={0}
              />
            ) : (
              <span className="view-data">{formValues?.[day]?.stock}</span>
            )}
          </Form.Item>
        );
      },
    };
  });

  return (
    <div className="stock-configuration-depends-on-day">
      <Table
        columns={columns}
        dataSource={getDataSource()}
        pagination={false}
        bordered
        locale={{ emptyText: ' ' }}
        className={getDataSource().length === 0 ? 'table-header-only' : ''}
      />
      {isEditMode && !validationErrors.stockError.isValid && (
        <span className="form-error-message">
          Please fix the errors indicated above to proceed :
          {validationErrors.stockError.errors.map((error, index) => (
            <div key={index}>- {error.message}</div>
          ))}
        </span>
      )}
      <Alert
        message="Toggle off the day you want to always see closed on the Inventory"
        type="info"
        showIcon
      />

      <Modal
        open={showToggleModal}
        onCancel={handleToggleModalCancel}
        footer={
          <>
            <Button onClick={handleToggleModalCancel}>Cancel</Button>
            <Button type="primary" onClick={handleToggleModalConfirm}>
              Yes
            </Button>
          </>
        }
        closable
        title="Delete manual stock data"
      >
        This action will delete any manual stock data entered for the days that
        have been closed. Are you sure you want to proceed?
      </Modal>
    </div>
  );
};
